import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './SubAccountDetail.css';

/**
 * 子账户详情组件
 * 
 * @param {Object} props
 * @param {String} props.accountId - 子账户ID
 * @param {Function} props.onBack - 返回回调
 * @param {Function} props.onRefresh - 刷新回调
 */
const SubAccountDetail = ({ accountId, onBack, onRefresh }) => {
  const [account, setAccount] = useState(null);
  const [records, setRecords] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    operationType: '',
    startDate: '',
    endDate: '',
    courtId: ''
  });

  const baseUrl = getApiBaseUrl();

  // 获取子账户详情
  const fetchAccountDetail = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/court-usage/sub-accounts/${accountId}`);
      if (response.data.success) {
        setAccount(response.data.data);
      } else {
        throw new Error(response.data.message || '获取账户详情失败');
      }
    } catch (err) {
      console.error('获取账户详情失败:', err);
      setError(err.response?.data?.message || err.message || '获取账户详情失败');
    }
  };

  // 获取场地列表
  useEffect(() => {
    const fetchCourts = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/court-usage/courts`);
        if (response.data.success) {
          setCourts(response.data.data || []);
        }
      } catch (err) {
        console.error('获取场地列表失败:', err);
      }
    };
    fetchCourts();
  }, []);

  // 获取记录列表
  const fetchRecords = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: pagination.limit,
        ...filters
      };
      
      // 移除空值
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get(`${baseUrl}/api/court-usage/sub-accounts/${accountId}/records`, { params });
      
      if (response.data.success) {
        setRecords(response.data.data.records || []);
        setPagination({
          page: response.data.data.page,
          limit: response.data.data.limit,
          total: response.data.data.total,
          totalPages: response.data.data.totalPages
        });
      } else {
        throw new Error(response.data.message || '获取记录失败');
      }
    } catch (err) {
      console.error('获取记录失败:', err);
      setError(err.response?.data?.message || err.message || '获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountId) {
      fetchAccountDetail();
      fetchRecords(1);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      fetchRecords(1);
    }
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    fetchRecords(newPage);
  };

  const handleRefresh = () => {
    fetchAccountDetail();
    fetchRecords(pagination.page);
    if (onRefresh) {
      onRefresh();
    }
  };

  const getOperationTypeText = (type) => {
    const typeMap = {
      'RECHARGE': '充值',
      'ALLOCATE': '分配',
      'CONSUME': '消费',
      'ADJUST': '调整'
    };
    return typeMap[type] || type;
  };

  const getOperationTypeColor = (type) => {
    const colorMap = {
      'RECHARGE': '#52c41a',
      'ALLOCATE': '#1890ff',
      'CONSUME': '#ff4d4f',
      'ADJUST': '#faad14'
    };
    return colorMap[type] || '#666';
  };

  if (loading && !account) {
    return (
      <div className="sub-account-detail">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error && !account) {
    return (
      <div className="sub-account-detail">
        <div className="error-message">{error}</div>
        <button className="back-button" onClick={onBack}>← 返回</button>
      </div>
    );
  }

  return (
    <div className="sub-account-detail">
      {/* 头部信息 */}
      <div className="detail-header">
        <button className="back-button" onClick={onBack}>← 返回</button>
        <h2>{account?.name || '子账户详情'}</h2>
        <button className="refresh-button" onClick={handleRefresh} title="刷新">
          🔄
        </button>
      </div>

      {/* 账户信息卡片 */}
      {account && (
        <div className="account-info-card">
          <div className="info-row">
            <span className="info-label">账户名称：</span>
            <span className="info-value">{account.name}</span>
          </div>
          {account.description && (
            <div className="info-row">
              <span className="info-label">账户说明：</span>
              <span className="info-value">{account.description}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">当前余额：</span>
            <span className={`info-value balance ${account.currentBalance < 0 ? 'negative' : 'positive'}`}>
              {account.currentBalance.toFixed(2)} 场时
            </span>
          </div>
          <div className="info-stats">
            <div className="stat-item">
              <span className="stat-label">总分配</span>
              <span className="stat-value">{account.totalAllocated?.toFixed(2) || '0.00'} 场时</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">总消费</span>
              <span className="stat-value">{account.totalConsumed?.toFixed(2) || '0.00'} 场时</span>
            </div>
          </div>
        </div>
      )}

      {/* 筛选器 */}
      <div className="filters-container">
        <div className="filter-group">
          <label>操作类型</label>
          <select
            value={filters.operationType}
            onChange={(e) => handleFilterChange('operationType', e.target.value)}
          >
            <option value="">全部</option>
            <option value="RECHARGE">充值</option>
            <option value="ALLOCATE">分配</option>
            <option value="CONSUME">消费</option>
            <option value="ADJUST">调整</option>
          </select>
        </div>

        <div className="filter-group">
          <label>场地</label>
          <select
            value={filters.courtId}
            onChange={(e) => handleFilterChange('courtId', e.target.value)}
          >
            <option value="">全部</option>
            {courts.map(court => (
              <option key={court._id} value={court._id}>
                {court.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>开始日期</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>结束日期</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
      </div>

      {/* 记录列表 */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : records.length === 0 ? (
        <div className="empty-state">
          <p>暂无记录</p>
        </div>
      ) : (
        <>
          <div className="records-table">
            <table>
              <thead>
                <tr>
                  <th>时间</th>
                  <th>操作类型</th>
                  <th>操作数量</th>
                  <th>操作前余额</th>
                  <th>操作后余额</th>
                  <th>说明</th>
                </tr>
              </thead>
              <tbody>
                {records.map(record => (
                  <tr key={record._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(record.operationDate).toLocaleDateString('zh-CN')}</td>
                    <td>
                      <span 
                        className="operation-type-badge"
                        style={{ color: getOperationTypeColor(record.operationType) }}
                      >
                        {getOperationTypeText(record.operationType)}
                      </span>
                    </td>
                    <td className={record.operationAmount >= 0 ? 'positive' : 'negative'}>
                      {record.operationAmount >= 0 ? '+' : ''}{record.operationAmount.toFixed(2)}
                    </td>
                    <td>{record.balanceBefore.toFixed(2)}</td>
                    <td className={record.balanceAfter < 0 ? 'negative' : ''}>
                      {record.balanceAfter.toFixed(2)}
                    </td>
                    <td className="description-cell" title={record.description}>
                      {record.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="page-button"
              >
                上一页
              </button>
              <span className="page-info">
                第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.total} 条
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="page-button"
              >
                下一页
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubAccountDetail;

